import { BankAccountRow, CreditCardRow } from './types';

// Account-label fallback chains shared by the account listings' pages, dialogs, table and drawer.
// The single implementation for every call site — copies of a fallback chain drift.
//
// Each helper's parameter is `Pick`ed from the row types rather than restated field by field, for the
// same reason those types are derived from the list responses: respell a field and it breaks here
// instead of quietly returning ''. Every field is optional and the argument nullable, because a call
// site may hold either union member, a raw list-response object (which carries no `Type` to narrow
// on), or an undefined `find()` result.

type AccountHolderFields = Partial<
  Pick<BankAccountRow, 'AccountHolderName' | 'RecipientName'> & Pick<CreditCardRow, 'CreditCardHolderName'>
>;

export function getAccountHolderLabel(account?: AccountHolderFields | null): string {
  return account?.AccountHolderName || account?.RecipientName || account?.CreditCardHolderName || '';
}

type FormattedAccountNumberFields = Partial<
  Pick<
    BankAccountRow,
    'AccountNumber' | 'Country' | 'FinancialInstitutionNumber' | 'BranchTransitNumber' | 'ABARoutingNumber'
  >
>;

// The bank-account number as it is displayed: prefixed with the domestic routing details the row
// actually carries — FI + transit (CA-style) or an ABA routing number (US-style) — and bare
// otherwise. Keyed off the routing fields present on the row rather than a stamped account-wide
// country: the country is one account-level value fanned onto every row, so a US bank account under
// a CA account (or any partner bank account, which has no real country to stamp) took the CA branch
// with empty FI/transit and rendered `--123456789`. The fields are the account's own facts and can't
// disagree with themselves. Callers that also have to cover cards layer that fallback on top.
export function getFormattedAccountNumber(account?: FormattedAccountNumberFields | null): string {
  const accountNumber = account?.AccountNumber || '';

  // Without this, a row with no account number still returns the truthy separators — `"--"` — which
  // defeats the callers' `|| '-'` and prints `--` where they meant to print `-`. The backend
  // COALESCEs these columns to '', so an empty bank row is reachable.
  if (accountNumber === '') return '';

  if (account?.FinancialInstitutionNumber || account?.BranchTransitNumber) {
    return `${account.FinancialInstitutionNumber || ''}-${account.BranchTransitNumber || ''}-${accountNumber}`;
  }

  if (account?.ABARoutingNumber) {
    return `${account.ABARoutingNumber}-${accountNumber}`;
  }

  return accountNumber;
}

// Picked field by field rather than `Partial<BankAccountRow> & Partial<CreditCardRow>`: intersecting
// the two union members intersects their `Type` discriminants too, and no row is both 'Bank Account'
// and 'Credit Card', so nothing would be assignable.
type MaskedAccountFields = Partial<Pick<BankAccountRow, 'AccountNumber'> & Pick<CreditCardRow, 'CreditCardNumber'>>;

// The account's masked identifier, for anywhere a row is named to the user. Both legs arrive already
// masked from the backend (`*****1234`), so there is no masking to do here — only the choice of which
// field a given row carries.
//
// Two legs, not four. `MaskedAccount` is unreachable — the handler strips it from the
// default-bank-account response and the list endpoint never selects it. So is any `Iban`, which the
// responses that do carry one spell `IBAN`. If IBAN display is wanted for the Operational listing's
// default-bank rows, add an `IBAN` leg, and put it first: an IBAN account has no FI/transit to
// prefix, so it outranks the account number rather than falling back to it.
export function getMaskedAccountLabel(account?: MaskedAccountFields | null): string {
  return account?.AccountNumber || account?.CreditCardNumber || '';
}
